import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { BookOpen, Users, ClipboardList, TrendingUp, Plus, Eye, Edit, Calendar, Sparkles, GraduationCap, UploadCloud, MessageSquare } from 'lucide-react';
import CurriculumManagement from '../components/teacher/CurriculumManagement';
import AssignmentUploadManager from '../components/teacher/AssignmentUploadManager';
import CounselorFeedbackList from '../components/shared/CounselorFeedbackList';
import NotificationBell from '../components/shared/NotificationBell';
import StudentAISummary from '../components/shared/StudentAISummary';
import { useFeedback } from '../hooks/useFeedback';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherDashboard = ({ user, onLogout }) => {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentProgress, setStudentProgress] = useState([]);
  const [studentReports, setStudentReports] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('students');
  
  // Assignment creation state
  const [newAssignment, setNewAssignment] = useState({
    title: '',
    description: '',
    subject: '',
    grade_level: '',
    assigned_students: [],
    due_date: ''
  });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Exam/Test state
  const [exams, setExams] = useState([]);
  const [newExam, setNewExam] = useState({
    title: '',
    description: '',
    subject: '',
    grade_level: '',
    due_date: '',
    time_limit_minutes: 60,
    questions: []
  });
  const [showExamDialog, setShowExamDialog] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState({
    question_type: 'mcq',
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: '',
    explanation: '',
    points: 1
  });
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const [institutionalStandard, setInstitutionalStandard] = useState('formal');
  
  // Submissions and feedback state
  const [showSubmissionsDialog, setShowSubmissionsDialog] = useState(false);
  const [selectedExamForSubmissions, setSelectedExamForSubmissions] = useState(null);
  const [examSubmissions, setExamSubmissions] = useState([]);
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [examGradeValue, setExamGradeValue] = useState('');
  const [examRatingValue, setExamRatingValue] = useState('');
  
  // Assignment submissions state
  const [showAssignmentSubmissionsDialog, setShowAssignmentSubmissionsDialog] = useState(false);
  const [selectedAssignmentForSubmissions, setSelectedAssignmentForSubmissions] = useState(null);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);
  const [showAssignmentGradeDialog, setShowAssignmentGradeDialog] = useState(false);
  const [selectedAssignmentSubmission, setSelectedAssignmentSubmission] = useState(null);
  const [assignmentGrade, setAssignmentGrade] = useState('');
  const [assignmentRating, setAssignmentRating] = useState('');
  const [assignmentFeedback, setAssignmentFeedback] = useState('');
  
  // Assignment edit state
  const [showEditAssignmentDialog, setShowEditAssignmentDialog] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const { feedback: counselorFeedback, unreadCount: feedbackUnread, markAsRead: markFeedbackRead } = useFeedback('teacher');

  useEffect(() => {
    fetchStudents();
    fetchAssignments();
    fetchExams();
  }, []);

  const fetchStudents = async () => {
    try {      const response = await axios.get(`${API}/teacher/students`);
      setStudents(response.data.students || []);
      if (response.data.students && response.data.students.length > 0) {
        setSelectedStudent(response.data.students[0]);
      }
    } catch (error) {
      console.error('Failed to fetch students', error);
      toast.error('Failed to load students');
    }
  };

  const fetchAssignments = async () => {
    try {      const response = await axios.get(`${API}/teacher/assignments`);
      setAssignments(response.data.assignments || []);
    } catch (error) {
      console.error('Failed to fetch assignments', error);
    }
  };

  const fetchExams = async () => {
    try {      const response = await axios.get(`${API}/teacher/exams`);
      setExams(response.data.exams || []);
    } catch (error) {
      console.error('Failed to fetch exams', error);
    }
  };

  const handleAIAssist = async (text) => {
    if (!text) {
      toast.error('Please enter some text first');
      return;
    }
    
    setAiAssistLoading(true);
    try {      const response = await axios.post(`${API}/teacher/ai-assist`, {
        text,
        mode: institutionalStandard,
        institutional_standard: institutionalStandard === 'custom' ? 'Professional Academic Standards' : null
      });
      
      setNewExam({...newExam, description: response.data.assisted});
      toast.success('Description improved by AI!');
    } catch (error) {
      toast.error('Failed to get AI assistance');
    } finally {
      setAiAssistLoading(false);
    }
  };

  const handleAddQuestion = () => {
    if (!currentQuestion.question_text) {
      toast.error('Please enter a question');
      return;
    }
    
    const questionWithId = {
      ...currentQuestion,
      id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    setNewExam({
      ...newExam,
      questions: [...newExam.questions, questionWithId]
    });
    
    // Reset form
    setCurrentQuestion({
      question_type: 'mcq',
      question_text: '',
      options: ['', '', '', ''],
      correct_answer: '',
      explanation: '',
      points: 1
    });
    
    toast.success('Question added!');
  };

  const handleRemoveQuestion = (questionId) => {
    setNewExam({
      ...newExam,
      questions: newExam.questions.filter(q => q.id !== questionId)
    });
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Convert file to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        setLoading(true);
        const base64Content = e.target.result.split(',')[1];        const response = await axios.post(`${API}/teacher/upload-excel-questions`, {
          file_content: base64Content
        }, { headers: { "Content-Type": "application/json" } });
        
        setNewExam({
          ...newExam,
          questions: [...newExam.questions, ...response.data.questions]
        });
        
        toast.success(response.data.message);
      } catch (error) {
        toast.error('Failed to parse Excel file');
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleCreateExam = async () => {
    if (!newExam.title || !newExam.subject || !newExam.grade_level || newExam.questions.length === 0) {
      toast.error('Please fill in all required fields and add at least one question');
      return;
    }
    
    const totalPoints = newExam.questions.reduce((sum, q) => sum + q.points, 0);
    
    try {
      setLoading(true);      await axios.post(`${API}/teacher/exams`, {
        ...newExam,
        total_points: totalPoints
      });
      
      toast.success('Exam created and assigned to students!');
      setShowExamDialog(false);
      setNewExam({
        title: '',
        description: '',
        subject: '',
        grade_level: '',
        due_date: '',
        time_limit_minutes: 60,
        questions: []
      });
      fetchExams();
    } catch (error) {
      toast.error('Failed to create exam');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedStudent) {
      fetchStudentProgress(selectedStudent.id);
      fetchStudentReports(selectedStudent.id);
    }
  }, [selectedStudent]);

  const fetchStudentProgress = async (studentId) => {
    try {      const response = await axios.get(`${API}/teacher/student/${studentId}/progress`);
      setStudentProgress(response.data.progress || []);
    } catch (error) {
      console.error('Failed to fetch student progress', error);
    }
  };

  const fetchStudentReports = async (studentId) => {
    try {      const response = await axios.get(`${API}/teacher/student/${studentId}/quiz-reports`);
      setStudentReports(response.data.reports || []);
    } catch (error) {
      console.error('Failed to fetch student reports', error);
    }
  };

  const handleCreateAssignment = async () => {
    if (!newAssignment.title || !newAssignment.subject || !newAssignment.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);      await axios.post(`${API}/teacher/assignments`, newAssignment);
      
      toast.success('Assignment created successfully!');
      setShowCreateDialog(false);
      setNewAssignment({
        title: '',
        description: '',
        subject: '',
        grade_level: '',
        assigned_students: [],
        due_date: ''
      });
      fetchAssignments();
    } catch (error) {
      console.error('Failed to create assignment', error);
      toast.error('Failed to create assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment? This will also delete all student submissions.')) {
      return;
    }
    
    try {      await axios.delete(`${API}/teacher/assignment/${assignmentId}`);
      toast.success('Assignment deleted successfully!');
      fetchAssignments();
    } catch (error) {
      toast.error('Failed to delete assignment');
    }
  };

  const handleDeleteExam = async (examId) => {
    if (!window.confirm('Are you sure you want to delete this exam? This will also delete all student submissions.')) {
      return;
    }
    
    try {      await axios.delete(`${API}/teacher/exam/${examId}`);
      toast.success('Exam deleted successfully!');
      fetchExams();
    } catch (error) {
      toast.error('Failed to delete exam');
    }
  };

  const handleDownloadExcelTemplate = () => {
    // Create Excel template data
    const template = `Question Type,Question Text,Option A,Option B,Option C,Option D,Correct Answer,Explanation,Points
mcq,What is 2+2?,3,4,5,6,B,Basic addition,1
true_false,The Earth is flat,True,False,,,False,The Earth is round,1
short_answer,What is the capital of France?,,,,,,Paris,France's capital city,2`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'exam_questions_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Template downloaded! Fill it and upload back.');
  };

  const handleViewExamSubmissions = async (exam) => {
    setSelectedExamForSubmissions(exam);
    try {      const response = await axios.get(`${API}/teacher/exam/${exam.id}/submissions`);
      setExamSubmissions(response.data.submissions || []);
      setShowSubmissionsDialog(true);
    } catch (error) {
      toast.error('Failed to fetch submissions');
    }
  };

  const handleProvideFeedback = (submission) => {
    setSelectedSubmission(submission);
    setFeedbackText(submission.teacher_feedback || '');
    setExamGradeValue(submission.teacher_grade || '');
    setExamRatingValue(submission.teacher_rating || '');
    setShowFeedbackDialog(true);
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackText.trim()) {
      toast.error('Please enter feedback');
      return;
    }

    try {
      setLoading(true);      await axios.post(
        `${API}/teacher/exam-submission/${selectedSubmission.id}/feedback`,
        {
          feedback: feedbackText,
          grade: examGradeValue ? parseFloat(examGradeValue) : null,
          rating: examRatingValue || null
        }
      );
      
      toast.success('Exam graded and feedback submitted!');
      setShowFeedbackDialog(false);
      // Refresh submissions
      if (selectedExamForSubmissions) {
        handleViewExamSubmissions(selectedExamForSubmissions);
      }
    } catch (error) {
      toast.error('Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAssignmentSubmissions = async (assignment) => {
    setSelectedAssignmentForSubmissions(assignment);
    setShowAssignmentSubmissionsDialog(true);
    
    try {
      const response = await axios.get(`${API}/teacher/assignment/${assignment.id}/submissions`);
      setAssignmentSubmissions(response.data.submissions || []);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  const handleGradeAssignment = (submission) => {
    setSelectedAssignmentSubmission(submission);
    setAssignmentGrade(submission.grade || '');
    setAssignmentRating(submission.rating || '');
    setAssignmentFeedback(submission.feedback || '');
    setShowAssignmentGradeDialog(true);
  };

  const handleSubmitAssignmentGrade = async () => {
    if (!assignmentGrade || !assignmentRating || !assignmentFeedback.trim()) {
      toast.error('Please enter score, rating, and feedback');
      return;
    }

    try {
      setLoading(true);
      await axios.put(
        `${API}/teacher/assignment/${selectedAssignmentForSubmissions.id}/submission/${selectedAssignmentSubmission.student_id}/grade`,
        null,
        {
          params: {
            grade: parseFloat(assignmentGrade),
            rating: assignmentRating,
            feedback: assignmentFeedback
          }
        }
      );
      
      toast.success('Assignment graded successfully!');
      setShowAssignmentGradeDialog(false);
      // Refresh submissions
      if (selectedAssignmentForSubmissions) {
        handleViewAssignmentSubmissions(selectedAssignmentForSubmissions);
      }
    } catch (error) {
      toast.error('Failed to submit grade');
    } finally {
      setLoading(false);
    }
  };

  const downloadAssignmentFile = (submission) => {
    if (!submission.file_data) {
      toast.error('No file attached to this submission');
      return;
    }
    
    try {
      const linkSource = `data:${submission.file_type};base64,${submission.file_data}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = submission.file_name || 'assignment.docx';
      downloadLink.click();
      toast.success('File downloaded!');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment({ ...assignment });
    setShowEditAssignmentDialog(true);
  };

  const handleSaveAssignmentEdit = async () => {
    if (!editingAssignment.title || !editingAssignment.description || !editingAssignment.subject) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      await axios.put(`${API}/teacher/assignment/${editingAssignment.id}`, editingAssignment);
      toast.success('Assignment updated successfully!');
      setShowEditAssignmentDialog(false);
      fetchAssignments();
    } catch (error) {
      toast.error('Failed to update assignment');
    } finally {
      setLoading(false);
    }
  };

  const calculateAverageScore = () => {
    if (studentProgress.length === 0) return 0;
    const total = studentProgress.reduce((sum, p) => sum + p.score, 0);
    return (total / studentProgress.length).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-100">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Teacher Dashboard</h1>
                <p className="text-sm text-gray-600">Welcome, {user?.name}!</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell onNavigate={(tab) => setActiveTab(tab)} />
              <Button onClick={onLogout} variant="outline" className="border-gray-300 hover:bg-gray-100">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {students.length === 0 ? (
          <Card className="p-12 text-center bg-white/90 backdrop-blur-sm border-0 shadow-lg">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No students assigned yet</h3>
            <p className="text-gray-600">Please contact a superuser to assign students to your class</p>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-6 mb-6">
              <TabsTrigger value="students" className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Students</span>
              </TabsTrigger>
              <TabsTrigger value="assignments" className="flex items-center gap-2">
                <ClipboardList className="w-4 h-4" />
                <span className="hidden sm:inline">Assignments</span>
              </TabsTrigger>
              <TabsTrigger value="exams" className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span className="hidden sm:inline">Exams</span>
              </TabsTrigger>
              <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="curriculum-tab">
                <GraduationCap className="w-4 h-4" />
                <span className="hidden sm:inline">Curriculum</span>
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex items-center gap-2" data-testid="upload-tab">
                <UploadCloud className="w-4 h-4" />
                <span className="hidden sm:inline">Upload</span>
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Analytics</span>
              </TabsTrigger>
              <TabsTrigger value="counselor-feedback" className="flex items-center gap-2" data-testid="teacher-feedback-tab">
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Counselor</span>
                {feedbackUnread > 0 && <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full" data-testid="feedback-unread-badge">{feedbackUnread}</span>}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="students" className="space-y-6">
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Student</label>
                <div className="flex gap-3 flex-wrap">
                  {students.map((student) => (
                    <button
                      key={student.id}
                      onClick={() => setSelectedStudent(student)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        selectedStudent?.id === student.id
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg'
                          : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      {student.name}
                    </button>
                  ))}
                </div>
              </div>

              {selectedStudent && (
                <>
                  {/* AI Progress Summary */}
                  <StudentAISummary studentId={selectedStudent.id} studentName={selectedStudent.name} />

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Average Score</p>
                          <p className="text-3xl font-bold text-blue-600">{calculateAverageScore()}%</p>
                        </div>
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                          <TrendingUp className="w-6 h-6 text-blue-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">Quizzes Taken</p>
                          <p className="text-3xl font-bold text-indigo-600">{studentProgress.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                          <ClipboardList className="w-6 h-6 text-indigo-600" />
                        </div>
                      </div>
                    </Card>

                    <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Student Info</p>
                        <p className="text-sm font-medium">{selectedStudent.name}</p>
                        <p className="text-xs text-gray-500">Grade: {selectedStudent.grade || 'N/A'}</p>
                        <p className="text-xs text-gray-500">Age: {selectedStudent.age || 'N/A'}</p>
                      </div>
                    </Card>
                  </div>

                  <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">Recent Quiz Results</h3>
                    {studentProgress.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <p>No quiz results yet</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {studentProgress.slice(0, 10).map((item, idx) => (
                          <Card key={idx} className="p-4 border border-gray-200">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h4 className="font-semibold text-gray-900 capitalize">{item.subject}</h4>
                                <p className="text-sm text-gray-600">
                                  {new Date(item.timestamp).toLocaleDateString()} at {new Date(item.timestamp).toLocaleTimeString()}
                                </p>
                              </div>
                              <Badge
                                className={`${
                                  item.score >= 80 ? 'bg-green-100 text-green-700' :
                                  item.score >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}
                              >
                                {item.score.toFixed(0)}%
                              </Badge>
                            </div>
                            <Progress value={item.score} className="h-2" />
                          </Card>
                        ))}
                      </div>
                    )}
                  </Card>

                  {studentReports.length > 0 && (
                    <Card className="bg-white/90 backdrop-blur-sm border-0 shadow-lg p-6">
                      <h3 className="text-lg font-semibold mb-4">Detailed Quiz Reports</h3>
                      <div className="space-y-3">
                        {studentReports.slice(0, 5).map((report, idx) => (
                          <Card key={idx} className="p-4 border border-gray-200 hover:border-blue-300 transition-colors">
                            <div className="flex justify-between items-center">
                              <div>
                                <h4 className="font-medium text-gray-900">{report.subject} - {report.difficulty}</h4>
                                <p className="text-sm text-gray-600">
                                  {report.correct_answers || 0}/{report.total_questions || 0} correct ({(report.score_percentage || 0).toFixed(0)}%)
                                </p>
                                <p className="text-xs text-gray-500">
                                  {new Date(report.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>

            <TabsContent value="assignments" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Assignments</h2>
                <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-blue-500 to-indigo-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Assignment
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Create New Assignment</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label htmlFor="title">Title *</Label>
                        <Input
                          id="title"
                          value={newAssignment.title}
                          onChange={(e) => setNewAssignment({...newAssignment, title: e.target.value})}
                          placeholder="Assignment title"
                        />
                      </div>
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <Label htmlFor="description">Description</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!newAssignment.description.trim()) {
                                toast.error('Please enter a description first');
                                return;
                              }
                              try {
                                const response = await axios.post(`${API}/teacher/ai-assist`, {
                                  text: newAssignment.description,
                                  mode: 'formal'
                                });
                                setNewAssignment({...newAssignment, description: response.data.improved_text});
                                toast.success('Description improved with AI!');
                              } catch (error) {
                                toast.error('Failed to improve description');
                              }
                            }}
                            className="text-xs"
                          >
                            ✨ Make Professional with AI
                          </Button>
                        </div>
                        <Textarea
                          id="description"
                          value={newAssignment.description}
                          onChange={(e) => setNewAssignment({...newAssignment, description: e.target.value})}
                          placeholder="Assignment description"
                          rows={4}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="subject">Subject *</Label>
                          <Select
                            value={newAssignment.subject}
                            onValueChange={(value) => setNewAssignment({...newAssignment, subject: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select subject" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Mathematics">Mathematics</SelectItem>
                              <SelectItem value="Science">Science</SelectItem>
                              <SelectItem value="English">English</SelectItem>
                              <SelectItem value="History">History</SelectItem>
                              <SelectItem value="Geography">Geography</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="grade">Grade Level</Label>
                          <Input
                            id="grade"
                            value={newAssignment.grade_level}
                            onChange={(e) => setNewAssignment({...newAssignment, grade_level: e.target.value})}
                            placeholder="e.g., 9th"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="due_date">Due Date *</Label>
                        <Input
                          id="due_date"
                          type="date"
                          value={newAssignment.due_date}
                          onChange={(e) => setNewAssignment({...newAssignment, due_date: e.target.value})}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateAssignment} disabled={loading}>
                        {loading ? 'Creating...' : 'Create Assignment'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {assignments.length === 0 ? (
                <Card className="p-12 text-center bg-white/90 backdrop-blur-sm">
                  <ClipboardList className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No assignments created yet</p>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {assignments.map((assignment) => (
                    <Card key={assignment.id} className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{assignment.title}</h3>
                            <Badge variant={assignment.status === 'active' ? 'default' : 'secondary'}>
                              {assignment.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{assignment.description}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-4 h-4" />
                              {assignment.subject}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Due: {new Date(assignment.due_date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditAssignment(assignment)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Details
                          </Button>
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleViewAssignmentSubmissions(assignment)}
                          >
                            <Users className="w-4 h-4 mr-1" />
                            Students Assignment
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteAssignment(assignment.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="exams" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Exams & Tests</h2>
                <Dialog open={showExamDialog} onOpenChange={setShowExamDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-blue-500 to-indigo-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Exam/Test
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Exam/Test</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="exam_title">Title *</Label>
                          <Input
                            id="exam_title"
                            value={newExam.title}
                            onChange={(e) => setNewExam({...newExam, title: e.target.value})}
                            placeholder="Midterm Exam"
                          />
                        </div>
                        <div>
                          <Label htmlFor="exam_subject">Subject *</Label>
                          <Select
                            value={newExam.subject}
                            onValueChange={(value) => setNewExam({...newExam, subject: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select subject" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Mathematics">Mathematics</SelectItem>
                              <SelectItem value="Science">Science</SelectItem>
                              <SelectItem value="English">English</SelectItem>
                              <SelectItem value="History">History</SelectItem>
                              <SelectItem value="Geography">Geography</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label htmlFor="exam_description">Description</Label>
                          <div className="flex gap-2 items-center">
                            <Select
                              value={institutionalStandard}
                              onValueChange={setInstitutionalStandard}
                            >
                              <SelectTrigger className="w-[200px]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="simplify">Simplify</SelectItem>
                                <SelectItem value="formal">Formal Academic</SelectItem>
                                <SelectItem value="concise">Concise</SelectItem>
                                <SelectItem value="custom">Custom Standard</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAIAssist(newExam.description)}
                              disabled={aiAssistLoading}
                            >
                              ✨ AI Assist
                            </Button>
                          </div>
                        </div>
                        <Textarea
                          id="exam_description"
                          value={newExam.description}
                          onChange={(e) => setNewExam({...newExam, description: e.target.value})}
                          placeholder="Exam description and instructions"
                          rows={3}
                        />
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label htmlFor="exam_grade">Grade Level *</Label>
                          <Input
                            id="exam_grade"
                            value={newExam.grade_level}
                            onChange={(e) => setNewExam({...newExam, grade_level: e.target.value})}
                            placeholder="9th"
                          />
                        </div>
                        <div>
                          <Label htmlFor="exam_due_date">Due Date *</Label>
                          <Input
                            id="exam_due_date"
                            type="date"
                            value={newExam.due_date}
                            onChange={(e) => setNewExam({...newExam, due_date: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label htmlFor="exam_time">Time Limit (minutes)</Label>
                          <Input
                            id="exam_time"
                            type="number"
                            value={newExam.time_limit_minutes}
                            onChange={(e) => setNewExam({...newExam, time_limit_minutes: parseInt(e.target.value)})}
                          />
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-3">Add Questions</h4>
                        
                        <div className="mb-4 flex gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            onClick={handleDownloadExcelTemplate}
                          >
                            📥 Download Template
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => document.getElementById('excel-upload').click()}
                            disabled={loading}
                          >
                            📁 Upload Excel
                          </Button>
                          <input
                            id="excel-upload"
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleExcelUpload}
                            style={{ display: 'none' }}
                          />
                          <span className="text-sm text-gray-500 flex items-center">
                            Or add questions manually below
                          </span>
                        </div>

                        <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                          <div>
                            <Label>Question Type</Label>
                            <Select
                              value={currentQuestion.question_type}
                              onValueChange={(value) => setCurrentQuestion({
                                ...currentQuestion,
                                question_type: value,
                                options: value === 'true_false' ? ['True', 'False'] : ['', '', '', '']
                              })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mcq">Multiple Choice (MCQ)</SelectItem>
                                <SelectItem value="true_false">True/False</SelectItem>
                                <SelectItem value="short_answer">Short Answer</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <Label>Question Text *</Label>
                            <Textarea
                              value={currentQuestion.question_text}
                              onChange={(e) => setCurrentQuestion({...currentQuestion, question_text: e.target.value})}
                              placeholder="Enter your question"
                              rows={2}
                            />
                          </div>

                          {currentQuestion.question_type === 'mcq' && (
                            <div>
                              <Label>Options</Label>
                              {currentQuestion.options.map((opt, idx) => (
                                <Input
                                  key={idx}
                                  value={opt}
                                  onChange={(e) => {
                                    const newOptions = [...currentQuestion.options];
                                    newOptions[idx] = e.target.value;
                                    setCurrentQuestion({...currentQuestion, options: newOptions});
                                  }}
                                  placeholder={`Option ${String.fromCharCode(65 + idx)}`}
                                  className="mb-2"
                                />
                              ))}
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label>Correct Answer *</Label>
                              {currentQuestion.question_type === 'mcq' ? (
                                <Select
                                  value={currentQuestion.correct_answer}
                                  onValueChange={(value) => setCurrentQuestion({...currentQuestion, correct_answer: value})}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select option" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="A">Option A</SelectItem>
                                    <SelectItem value="B">Option B</SelectItem>
                                    <SelectItem value="C">Option C</SelectItem>
                                    <SelectItem value="D">Option D</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : currentQuestion.question_type === 'true_false' ? (
                                <Select
                                  value={currentQuestion.correct_answer}
                                  onValueChange={(value) => setCurrentQuestion({...currentQuestion, correct_answer: value})}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="True">True</SelectItem>
                                    <SelectItem value="False">False</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  value={currentQuestion.correct_answer}
                                  onChange={(e) => setCurrentQuestion({...currentQuestion, correct_answer: e.target.value})}
                                  placeholder="Expected answer"
                                />
                              )}
                            </div>
                            <div>
                              <Label>Points</Label>
                              <Input
                                type="number"
                                value={currentQuestion.points}
                                onChange={(e) => setCurrentQuestion({...currentQuestion, points: parseInt(e.target.value)})}
                                min="1"
                              />
                            </div>
                          </div>

                          <div>
                            <Label>Explanation (Optional)</Label>
                            <Textarea
                              value={currentQuestion.explanation}
                              onChange={(e) => setCurrentQuestion({...currentQuestion, explanation: e.target.value})}
                              placeholder="Explain the correct answer"
                              rows={2}
                            />
                          </div>

                          <Button onClick={handleAddQuestion} variant="outline" className="w-full">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Question
                          </Button>
                        </div>

                        {newExam.questions.length > 0 && (
                          <div className="mt-4">
                            <h5 className="font-medium mb-2">Added Questions ({newExam.questions.length})</h5>
                            <div className="space-y-2 max-h-[200px] overflow-y-auto">
                              {newExam.questions.map((q, idx) => (
                                <div key={q.id} className="flex justify-between items-center p-2 border rounded">
                                  <span className="text-sm">
                                    {idx + 1}. {q.question_text.substring(0, 50)}... ({q.points} pts)
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleRemoveQuestion(q.id)}
                                  >
                                    ❌
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowExamDialog(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateExam} disabled={loading}>
                        {loading ? 'Creating...' : 'Create Exam'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {exams.length === 0 ? (
                <Card className="p-12 text-center bg-white/90 backdrop-blur-sm">
                  <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No exams created yet</p>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {exams.map((exam) => (
                    <Card key={exam.id} className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{exam.title}</h3>
                            <Badge variant={exam.status === 'active' ? 'default' : 'secondary'}>
                              {exam.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{exam.description}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <BookOpen className="w-4 h-4" />
                              {exam.subject}
                            </span>
                            <span>Grade: {exam.grade_level}</span>
                            <span>{exam.questions.length} questions</span>
                            <span>{exam.total_points} points</span>
                            <span>{exam.time_limit_minutes} min</span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              Due: {new Date(exam.due_date).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewExamSubmissions(exam)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Submissions
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDeleteExam(exam.id)}
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="analytics" className="space-y-6">
              <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Class Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{students.length}</p>
                    <p className="text-sm text-gray-600">Total Students</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-indigo-600">{assignments.length}</p>
                    <p className="text-sm text-gray-600">Active Assignments</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-purple-600">
                      {studentProgress.length}
                    </p>
                    <p className="text-sm text-gray-600">Total Quizzes</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-white/90 backdrop-blur-sm border-0 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Student Performance Summary</h3>
                <div className="space-y-3">
                  {students.map((student) => (
                    <div key={student.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <span className="font-medium">{student.name}</span>
                      <Badge variant="outline">{student.grade}</Badge>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="curriculum" className="space-y-6">
              <CurriculumManagement />
            </TabsContent>

            <TabsContent value="upload" className="space-y-6">
              <AssignmentUploadManager />
            </TabsContent>

            <TabsContent value="counselor-feedback" className="space-y-6" data-testid="teacher-feedback-content">
              <div className="flex items-center gap-3 mb-4">
                <MessageSquare className="w-5 h-5 text-teal-600" />
                <h2 className="text-2xl font-bold text-gray-900">Counselor Feedback</h2>
              </div>
              <CounselorFeedbackList
                feedback={counselorFeedback}
                currentUserId={user?.id}
                onMarkRead={markFeedbackRead}
              />
            </TabsContent>
          </Tabs>
        )}

        {/* Exam Submissions Dialog */}
        <Dialog open={showSubmissionsDialog} onOpenChange={setShowSubmissionsDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Exam Submissions - {selectedExamForSubmissions?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {examSubmissions.length === 0 ? (
                <p className="text-center text-gray-600 py-8">No submissions yet</p>
              ) : (
                <div className="space-y-4">
                  {examSubmissions.map((submission) => (
                    <Card key={submission.id} className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg mb-2">{submission.student_name}</h4>
                          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                            <div>
                              <span className="text-gray-600">Score: </span>
                              <span className="font-semibold">{submission.total_score ?? 0} / {submission.percentage > 0 ? ((submission.total_score || 0) / (submission.percentage / 100)).toFixed(0) : 0} points</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Percentage: </span>
                              <Badge className={
                                submission.percentage >= 90 ? 'bg-green-500' :
                                submission.percentage >= 75 ? 'bg-blue-500' :
                                submission.percentage >= 60 ? 'bg-yellow-500' :
                                'bg-red-500'
                              }>
                                {(submission.percentage ?? 0).toFixed(1)}%
                              </Badge>
                            </div>
                            <div>
                              <span className="text-gray-600">Time Taken: </span>
                              <span>{Math.floor(submission.time_taken_seconds / 60)}m {submission.time_taken_seconds % 60}s</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Submitted: </span>
                              <span>{new Date(submission.submitted_at).toLocaleString()}</span>
                            </div>
                          </div>
                          
                          {submission.teacher_feedback && (
                            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                              <div className="flex gap-4 mb-1">
                                {submission.teacher_grade && (
                                  <p className="text-sm font-medium text-yellow-900">Teacher Grade: {submission.teacher_grade}/100</p>
                                )}
                                {submission.teacher_rating && (
                                  <Badge className="bg-purple-500">{submission.teacher_rating}</Badge>
                                )}
                              </div>
                              <p className="text-sm font-medium text-yellow-900 mb-1">Feedback:</p>
                              <p className="text-sm text-gray-700">{submission.teacher_feedback}</p>
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleProvideFeedback(submission)}
                          data-testid={`grade-exam-btn-${submission.id}`}
                        >
                          {submission.teacher_reviewed ? 'Edit Review' : 'Grade & Review'}
                        </Button>
                      </div>
                      
                      {/* Detailed Answers */}
                      <div className="mt-4 border-t pt-4">
                        <h5 className="font-medium mb-3">Question-by-Question Results</h5>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {submission.answers?.map((answer, idx) => (
                            <div key={idx} className={`p-3 rounded text-sm ${
                              answer.is_correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                            }`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="font-medium mb-1">Q{idx + 1}: {answer.question_text}</p>
                                  <p className="text-gray-600">
                                    <span className="font-medium">Student Answer:</span> {answer.student_answer || 'No answer'}
                                  </p>
                                  {!answer.is_correct && (
                                    <p className="text-green-700">
                                      <span className="font-medium">Correct Answer:</span> {answer.correct_answer}
                                    </p>
                                  )}
                                </div>
                                <Badge className={answer.is_correct ? 'bg-green-500' : 'bg-red-500'}>
                                  {answer.points_earned}/{answer.max_points}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Exam Feedback & Grading Dialog */}
        <Dialog open={showFeedbackDialog} onOpenChange={setShowFeedbackDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Grade & Feedback - {selectedSubmission?.student_name}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {selectedSubmission && (
                <div className="p-4 bg-gray-50 rounded">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="font-medium">Auto Score:</span> {(selectedSubmission.percentage ?? 0).toFixed(1)}%</div>
                    <div><span className="font-medium">Points:</span> {selectedSubmission.total_score ?? 0}</div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Teacher Grade (out of 100)</Label>
                  <Input
                    type="number" min="0" max="100"
                    value={examGradeValue}
                    onChange={(e) => setExamGradeValue(e.target.value)}
                    placeholder="Enter grade"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>Rating</Label>
                  <Select value={examRatingValue} onValueChange={setExamRatingValue}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select rating" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Excellent">Excellent</SelectItem>
                      <SelectItem value="Good">Good</SelectItem>
                      <SelectItem value="Average">Average</SelectItem>
                      <SelectItem value="Needs Improvement">Needs Improvement</SelectItem>
                      <SelectItem value="Poor">Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Feedback Message *</Label>
                <Textarea
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Provide constructive feedback to the student..."
                  rows={5}
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFeedbackDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmitFeedback} disabled={loading} data-testid="submit-exam-feedback-btn">
                {loading ? 'Submitting...' : 'Submit Grade & Feedback'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assignment Submissions Dialog */}
        <Dialog open={showAssignmentSubmissionsDialog} onOpenChange={setShowAssignmentSubmissionsDialog}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Assignment Submissions - {selectedAssignmentForSubmissions?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {assignmentSubmissions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>No submissions yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {assignmentSubmissions.map((submission) => (
                    <Card key={submission.id} className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{submission.student_name}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Submitted: {new Date(submission.submitted_at).toLocaleString()}
                          </p>
                          
                          {/* Auto-score summary for question paper submissions */}
                          {submission.submission_type === 'question_paper' && (
                            <div className="mt-2 flex flex-wrap gap-3">
                              <Badge className="bg-blue-600">Auto: {submission.earned_marks ?? 0}/{submission.total_marks || 0} ({submission.score_percentage ?? 0}%)</Badge>
                              {submission.ai_earned_marks !== undefined && (
                                <Badge className="bg-amber-600">AI Eval: +{(submission.ai_earned_marks ?? 0).toFixed(1)} pts</Badge>
                              )}
                              {submission.teacher_reviewed && (
                                <Badge className="bg-green-600">Teacher Reviewed</Badge>
                              )}
                            </div>
                          )}
                          
                          {submission.file_name && (
                            <div className="mt-2 flex items-center gap-2">
                              <Badge className="bg-blue-500">
                                {submission.file_name}
                              </Badge>
                              <Button size="sm" variant="outline" onClick={() => downloadAssignmentFile(submission)}>
                                Download
                              </Button>
                            </div>
                          )}

                          {submission.content && (
                            <div className="mt-2 p-3 bg-gray-50 rounded text-sm">
                              <p className="font-medium">Student Notes:</p>
                              <p className="text-gray-700 mt-1">{submission.content}</p>
                            </div>
                          )}
                          
                          {/* Question Paper Answers */}
                          {submission.submission_type === 'question_paper' && submission.scored_answers && selectedAssignmentForSubmissions?.question_paper && (
                            <div className="mt-3 border rounded-lg overflow-hidden">
                              <div className="bg-gray-100 px-3 py-2 text-sm font-medium">Student Answers</div>
                              <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto">
                                {(selectedAssignmentForSubmissions.question_paper.sections || []).map((section, sIdx) => (
                                  <div key={sIdx}>
                                    <p className="text-xs font-semibold text-gray-500 mb-1">{section.name}</p>
                                    {(section.questions || []).map((q, qIdx) => {
                                      const key = `${sIdx}-${qIdx}`;
                                      const scored = submission.scored_answers?.[key];
                                      const aiEval = submission.ai_evaluation?.[key];
                                      return (
                                        <div key={key} className={`p-2 mb-1 rounded text-sm border ${
                                          scored?.is_correct === true ? 'bg-green-50 border-green-200' :
                                          scored?.is_correct === false ? 'bg-red-50 border-red-200' :
                                          'bg-gray-50 border-gray-200'
                                        }`} data-testid={`teacher-answer-${key}`}>
                                          <p className="font-medium">Q{q.number || qIdx + 1}: {q.text}</p>
                                          <p className="mt-1 text-gray-700">
                                            <span className="font-medium">Answer:</span> {scored?.answer || submission.answers?.[key] || '—'}
                                          </p>
                                          <div className="flex flex-wrap gap-2 mt-1">
                                            {scored?.is_correct !== null && (
                                              <Badge className={scored?.is_correct ? 'bg-green-500' : 'bg-red-500'}>
                                                {scored?.is_correct ? 'Correct' : 'Incorrect'}
                                              </Badge>
                                            )}
                                            {scored?.auto_graded && (
                                              <span className="text-xs text-gray-400">Auto-graded</span>
                                            )}
                                            {aiEval && (
                                              <Badge variant="outline" className="text-xs text-amber-700 border-amber-300">
                                                AI: {aiEval.suggested_score}/{scored?.marks} - {aiEval.feedback}
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Current grade display */}
                          {submission.grade && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                              <div className="flex gap-4 mb-2">
                                <p className="text-sm font-medium text-green-900">
                                  Score: {submission.grade}/100
                                </p>
                                {submission.rating && (
                                  <Badge className="bg-purple-500">
                                    {submission.rating}
                                  </Badge>
                                )}
                              </div>
                              {submission.feedback && (
                                <>
                                  <p className="text-sm font-medium text-green-900 mt-2">Your Feedback:</p>
                                  <p className="text-sm text-gray-700">{submission.feedback}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(submission.feedback_date).toLocaleString()}
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={() => handleGradeAssignment(submission)}
                          size="sm"
                          className="ml-4"
                          data-testid={`grade-assignment-btn-${submission.student_id}`}
                        >
                          {submission.teacher_reviewed ? 'Edit Grade' : 'Grade & Review'}
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignmentSubmissionsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assignment Grading Dialog */}
        <Dialog open={showAssignmentGradeDialog} onOpenChange={setShowAssignmentGradeDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                Grade Assignment - {selectedAssignmentSubmission?.student_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedAssignmentSubmission?.file_name && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm font-medium text-blue-900">Submitted File:</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className="bg-blue-500">📄 {selectedAssignmentSubmission.file_name}</Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadAssignmentFile(selectedAssignmentSubmission)}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="assignment-grade">Score (out of 100) *</Label>
                  <Input
                    id="assignment-grade"
                    type="number"
                    min="0"
                    max="100"
                    value={assignmentGrade}
                    onChange={(e) => setAssignmentGrade(e.target.value)}
                    placeholder="Enter score"
                    className="mt-2"
                  />
                </div>
                
                <div>
                  <Label htmlFor="assignment-rating">Rating *</Label>
                  <Select value={assignmentRating} onValueChange={setAssignmentRating}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select rating" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Excellent">⭐⭐⭐⭐⭐ Excellent</SelectItem>
                      <SelectItem value="Good">⭐⭐⭐⭐ Good</SelectItem>
                      <SelectItem value="Average">⭐⭐⭐ Average</SelectItem>
                      <SelectItem value="Needs Improvement">⭐⭐ Needs Improvement</SelectItem>
                      <SelectItem value="Poor">⭐ Poor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="assignment-feedback">Feedback *</Label>
                <Textarea
                  id="assignment-feedback"
                  value={assignmentFeedback}
                  onChange={(e) => setAssignmentFeedback(e.target.value)}
                  placeholder="Provide detailed feedback to the student..."
                  rows={6}
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAssignmentGradeDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmitAssignmentGrade} disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Grade & Feedback'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Assignment Dialog */}
        <Dialog open={showEditAssignmentDialog} onOpenChange={setShowEditAssignmentDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>View & Edit Assignment Details</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-title">Title *</Label>
                <Input
                  id="edit-title"
                  value={editingAssignment?.title || ''}
                  onChange={(e) => setEditingAssignment({...editingAssignment, title: e.target.value})}
                  placeholder="Assignment title"
                  className="mt-2"
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label htmlFor="edit-description">Description *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!editingAssignment?.description?.trim()) {
                        toast.error('Please enter a description first');
                        return;
                      }
                      try {
                        const response = await axios.post(`${API}/teacher/ai-assist`, {
                          text: editingAssignment.description,
                          mode: 'formal'
                        });
                        setEditingAssignment({...editingAssignment, description: response.data.improved_text});
                        toast.success('Description improved with AI!');
                      } catch (error) {
                        toast.error('Failed to improve description');
                      }
                    }}
                    className="text-xs"
                  >
                    ✨ Make Professional with AI
                  </Button>
                </div>
                <Textarea
                  id="edit-description"
                  value={editingAssignment?.description || ''}
                  onChange={(e) => setEditingAssignment({...editingAssignment, description: e.target.value})}
                  placeholder="Assignment description"
                  rows={4}
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-subject">Subject *</Label>
                <Input
                  id="edit-subject"
                  value={editingAssignment?.subject || ''}
                  onChange={(e) => setEditingAssignment({...editingAssignment, subject: e.target.value})}
                  placeholder="Subject"
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-grade">Grade Level *</Label>
                <Input
                  id="edit-grade"
                  value={editingAssignment?.grade_level || ''}
                  onChange={(e) => setEditingAssignment({...editingAssignment, grade_level: e.target.value})}
                  placeholder="e.g., 9th, 10th, 11th"
                  className="mt-2"
                />
              </div>
              
              <div>
                <Label htmlFor="edit-due-date">Due Date *</Label>
                <Input
                  id="edit-due-date"
                  type="date"
                  value={editingAssignment?.due_date?.split('T')[0] || ''}
                  onChange={(e) => setEditingAssignment({...editingAssignment, due_date: e.target.value})}
                  className="mt-2"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditAssignmentDialog(false)}>
                Close
              </Button>
              <Button onClick={handleSaveAssignmentEdit} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TeacherDashboard;
